import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Text,
  VStack,
  Input,
  Select,
  FormControl,
  FormLabel,
  Textarea,
  useToast,
  Switch,
  FormErrorMessage,
} from "@chakra-ui/react";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { fetchOneCustomerSegment, editOneCustomerSegment } from "../../../actions/management.action";
import { Loading } from "../../../components/Loading";

const CustomerEdit = () => {
  const toast = useToast();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    whatsapp: "",
    city: "",
    gender: "",
    language: "",
    comments: "",
    isCorporate: false,
    type: "",
    rating: 0,
    notes: "",
    status: "Active",
    companyName: "",
    accountCategory: "",
    preferredChannels: [],
  });

  const getCustomerDetails = async () => {
    try {
      let res = await fetchOneCustomerSegment(id);
      const customer = res.data;
      
      setFormData({
        name: customer?.name || "",
        email: customer?.email || "",
        mobile: customer?.mobile || "",
        whatsapp: customer?.whatsapp || "",
        city: customer?.city || "",
        gender: customer?.gender || "",
        language: customer?.language || "",
        comments: customer?.comments || "",
        isCorporate: customer?.isCorporate || false,
        type: customer?.type || "",
        rating: customer?.rating || 0,
        notes: customer?.notes || "",
        status: customer?.status || "Active",
        companyName: customer?.corporateDetails?.companyName || "",
        accountCategory: customer?.accountCategory || "",
        preferredChannels: customer?.preferredChannels || [],
      });
    } catch (error) {
      console.log(error);
      toast({
        title: "Error",
        description: "Failed to fetch customer details",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    
    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    }
    
    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields correctly",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setSubmitting(true);
    
    try {
      const submitData = {
        name: formData.name,
        email: formData.email,
        mobile: formData.mobile,
        whatsapp: formData.whatsapp,
        city: formData.city,
        gender: formData.gender,
        language: formData.language,
        comments: formData.comments,
        isCorporate: formData.isCorporate,
        type: formData.type,
        rating: parseFloat(formData.rating),
        notes: formData.notes,
        status: formData.status,
      };
      
      if (formData.isCorporate && formData.companyName) {
        submitData.companyName = formData.companyName;
      }
      
      await editOneCustomerSegment(submitData, id);
      
      toast({
        title: "Success",
        description: "Customer updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
      navigate(`/management/customer`);
    } catch (error) {
      console.log(error);
      toast({
        title: "Error",
        description: error.message || "Failed to update customer",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  useEffect(() => {
    if (id) {
      getCustomerDetails();
    }
  }, [id]);

  if (loading) {
    return <Loading />;
  }

  return (
    <Box px={4} py={6}>
      <Flex
        justifyContent={{ base: "center", md: "space-between" }}
        alignItems={{ base: "flex-start", md: "center" }}
        mb={6}
        flexDir={{ base: "column", md: "row" }}
      >
        <BreadCrumbMaker
          items={[
            { link: "/management", title: "Operations Setup" },
            { link: "/management/customer", title: "Customers" },
            {
              link: `/management/customer/${id}`,
              title: formData?.name || "Details",
            },
            {
              link: `/management/customer/edit/${id}`,
              title: "Edit",
              on: true,
            },
          ]}
        />
      </Flex>

      <Box as="form" onSubmit={handleSubmit}>
        <Box p={6} bg={"white"} borderRadius={"lg"} shadow="sm">
          {/* Basic Information */}
          <Box
            border="2px"
            borderColor="gray.200"
            p={6}
            shadow="sm"
            bg={"white"}
            borderRadius={"lg"}
            mb={6}
          >
            <Heading fontSize="lg" mb={6} color={"#1A202C"}>
              Basic Information
            </Heading>
            
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6}>
              <FormControl isRequired isInvalid={errors.name}>
                <FormLabel fontSize="sm" fontWeight="medium">Full Name</FormLabel>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter customer name"
                  size="md"
                />
                <FormErrorMessage>{errors.name}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={errors.email}>
                <FormLabel fontSize="sm" fontWeight="medium">Email</FormLabel>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="customer@example.com"
                  size="md"
                />
                <FormErrorMessage>{errors.email}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={errors.mobile}>
                <FormLabel fontSize="sm" fontWeight="medium">Mobile Number</FormLabel>
                <Input
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="+91XXXXXXXXXX"
                  size="md"
                />
                <FormErrorMessage>{errors.mobile}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">WhatsApp Number</FormLabel>
                <Input
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleChange}
                  placeholder="+91XXXXXXXXXX"
                  size="md"
                />
              </FormControl>

              <FormControl isRequired isInvalid={errors.city}>
                <FormLabel fontSize="sm" fontWeight="medium">City</FormLabel>
                <Input
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Enter city"
                  size="md"
                />
                <FormErrorMessage>{errors.city}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Gender</FormLabel>
                <Select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  placeholder="Select gender"
                  size="md"
                >
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Language</FormLabel>
                <Input
                  name="language"
                  value={formData.language}
                  onChange={handleChange}
                  placeholder="e.g., English, Hindi"
                  size="md"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Status</FormLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  size="md"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </Select>
              </FormControl>
            </Grid>
          </Box>

          {/* Account Details */}
          <Box
            border="2px"
            borderColor="gray.200"
            p={6}
            shadow="sm"
            bg={"white"}
            borderRadius={"lg"}
            mb={6}
          >
            <Heading fontSize="lg" mb={6} color={"#1A202C"}>
              Account Details
            </Heading>
            
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6}>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Customer Type</FormLabel>
                <Select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  placeholder="Select type"
                  size="md"
                >
                  <option value="Individual">Individual</option>
                  <option value="Corporate">Corporate</option>
                  <option value="VIP">VIP</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Rating</FormLabel>
                <Input
                  name="rating"
                  type="number"
                  step="0.1"
                  min="0"
                  max="5"
                  value={formData.rating}
                  onChange={handleChange}
                  placeholder="0.0"
                  size="md"
                />
              </FormControl>

              <FormControl display="flex" alignItems="center" mt={4}>
                <FormLabel fontSize="sm" fontWeight="medium" mb={0}>
                  Corporate Customer
                </FormLabel>
                <Switch
                  name="isCorporate"
                  isChecked={formData.isCorporate}
                  onChange={handleChange}
                  colorScheme="blue"
                  size="lg"
                />
              </FormControl>

              {formData.isCorporate && (
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Company Name</FormLabel>
                  <Input
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="Enter company name"
                    size="md"
                  />
                </FormControl>
              )}
            </Grid>
          </Box>

          {/* Additional Information */}
          <Box
            border="2px"
            borderColor="gray.200"
            p={6}
            shadow="sm"
            bg={"white"}
            borderRadius={"lg"}
            mb={6}
          >
            <Heading fontSize="lg" mb={6} color={"#1A202C"}>
              Additional Information
            </Heading>
            
            <VStack spacing={6} alignItems="stretch">
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Comments</FormLabel>
                <Textarea
                  name="comments"
                  value={formData.comments}
                  onChange={handleChange}
                  placeholder="Add any comments about the customer"
                  rows={4}
                  size="md"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Notes</FormLabel>
                <Textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Add internal notes"
                  rows={4}
                  size="md"
                />
              </FormControl>
            </VStack>
          </Box>

          {/* Action Buttons */}
          <Flex gap={4} justifyContent="flex-end" mt={6}>
            <Button
              as={Link}
              to={`/management/customer/${id}`}
              variant="outline"
              colorScheme="gray"
              size="md"
              px={8}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="blue"
              size="md"
              px={8}
              isLoading={submitting}
              loadingText="Updating..."
            >
              Update Customer
            </Button>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerEdit;